import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useRouter } from "next/navigation";
import { useSelector } from "react-redux";
import { hasChildRouteAccess } from "@/routes/nav-child-routes";
import { getNavLeafKey, sanitizePathForLeaf } from "@/routes/nav-route-persistence";

const SidebarMenu = ({
  anchorEl,
  isMenuOpen,
  handleMenuClose,
  menuOptions = [],
  pathname = "",
  roles = {},
  parentName = "",
  parentTitle = "",
  isTourDriven = false,
}) => {
  const router = useRouter();
  const savedRoutes = useSelector((state) => state.routeHistory?.routes ?? {});

  const handleClick = (e, item) => {
    handleMenuClose(e);
    const target = item?.path;
    if (!target) return;
    // Always restore the last-visited path for this leaf (including query params).
    // Fall back to the canonical base path if nothing is saved yet.
    const leafKey = getNavLeafKey(item);
    const destination = sanitizePathForLeaf(leafKey, savedRoutes[leafKey]) ?? target;
    if (pathname === destination) return;
    setTimeout(() => router.push(destination), 300);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={handleMenuClose}
      disableScrollLock
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        root: {
          // When tour-driven, render above Intro.js overlay (999999) and helperLayer (9999999)
          style: isTourDriven ? { zIndex: 10000001 } : undefined,
        },
        paper: {
          sx: {
            left: "88px !important",
            backgroundColor: "var(--palette-primary_main, #3c2884)",
            color: "#fff",
            minWidth: "180px",
            mx: "0",
            my: "0",
            py: "0",
            borderRadius: "10px",
            boxShadow: "9px 11px 17px 0px rgba(0,0,0,0.25)",
            border: "none",
            outline: "none",
            backgroundImage: "none",
            overflow: "hidden"
          }
        },
        list: {
          sx: {
            padding: 0
          }
        }
      }}
    >
      <Box sx={{ 
        px: "16px", 
        py: "12px", 
        borderBottom: "1px solid rgba(255,255,255,0.15)"
      }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "16px", letterSpacing: "-0.25px", lineHeight: "1" }}>
          {parentTitle}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", px: "8px", py: "8px" }}>
        {menuOptions.map((opt, index) => {
          // If a specific child role is undefined, fall back to the parent's view permission
          if (!hasChildRouteAccess(parentName, opt, roles)) return null;

          const isSelected = pathname?.endsWith(opt?.path);

          return (
          <MenuItem
              key={index}
              onClick={(e) => handleClick(e, opt)}
              sx={{
                justifyContent: "flex-start",
                px: "12px",
                py: "8px",
                borderRadius: "5px",
                backgroundColor: isSelected ? "rgba(255,255,255,0.15)" : "transparent",
                "&:hover": {
                  backgroundColor: isSelected ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.1)",
                }
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 400, color: "white", lineHeight: "1.2" }}>
                {opt.title}
              </Typography>
            </MenuItem>
          );
        })}
      </Box>
    </Menu>
  );
};
export default SidebarMenu;
